/**
 * Unified PatientPanel shell — tabbed layout with toolbar, extraction
 * progress banner, and urgency-colored tab dots.
 *
 * This replaces PatientPanelColumn.tsx with a cleaner prop interface
 * (PatientPanelData) and composable tab components.
 */

import { FC, useState, useRef, useEffect } from 'react'
import {
  Loader2,
  MoreHorizontal,
  Edit3,
  RefreshCw,
  Trash2,
  PanelLeftClose,
} from 'lucide-react'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import type { PatientPanelData, PanelTab } from './types'
import { SummaryTab } from './tabs/SummaryTab'
import { ResultsTab } from './tabs/ResultsTab'
import { OrdersTab } from './tabs/OrdersTab'
import { MedsTab } from './tabs/MedsTab'
import { IPASSTab } from './tabs/IPASSTab'
import { PanelErrorBoundary } from './PanelErrorBoundary'

// ────────────────────────────────────────────────────────
// Props
// ────────────────────────────────────────────────────────

interface PatientPanelProps {
  data: PatientPanelData
  onCollapseToHeader?: () => void
  onEdit?: () => void
  onClear?: () => Promise<void>
  onReconcile?: () => void
  isReconciling?: boolean
  showToolbar?: boolean
  extractionProgress?: { isActive: boolean; message: string }
}

// ────────────────────────────────────────────────────────
// Tab dot helpers
// ────────────────────────────────────────────────────────

type DotColor = string | null

function summaryDot(data: PatientPanelData): DotColor {
  return data.summary ? 'bg-green-400' : null
}

function resultsDot(data: PatientPanelData): DotColor {
  const hasVitals = data.vitals && data.vitals.length > 0
  const hasLabs = data.labs && data.labs.length > 0
  const hasImaging = data.imaging && data.imaging.length > 0

  if (!hasVitals && !hasLabs && !hasImaging) return null

  // Check for critical flags across vitals and labs
  const hasCritical =
    (data.vitals?.some(v => v.flag === 'high' || v.flag === 'low') ?? false) ||
    (data.labs?.some(g => g.labs.some(l => l.flag === 'high' || l.flag === 'low')) ?? false)

  if (hasCritical) return 'bg-red-400'

  // Check for any abnormals (same condition — vitals/labs with flags)
  const hasAbnormal =
    (data.vitals?.some(v => v.flag !== null) ?? false) ||
    (data.labs?.some(g => g.labs.some(l => l.flag !== null)) ?? false)

  if (hasAbnormal) return 'bg-amber-400'

  return 'bg-green-400'
}

function ordersDot(data: PatientPanelData): DotColor {
  if (!data.orders?.sections || data.orders.sections.length === 0) return null
  if (data.isGenerating) return 'bg-amber-400 animate-pulse'
  return 'bg-green-400'
}

/** One warning per unknown status per page load, not one per render: medsDot
 *  runs on every render of the tab strip, so a plain boolean latch would also
 *  swallow the SECOND unknown status a session sees — the one that says the
 *  drift is a pattern, not a typo. */
const warnedUnknownMedsStatuses = new Set<string>()

function medsDot(data: PatientPanelData): DotColor {
  const hasMeds =
    data.medications &&
    Object.values(data.medications).some(arr => Array.isArray(arr) && arr.length > 0)

  // An empty list is normally nothing to flag — but a reconciliation that failed,
  // fell short, or came back in a state this build cannot read is exactly the
  // case where the list can be empty BECAUSE of it, and the tab strip is the
  // only place a clinician sees that without opening the tab. Routed through the
  // SAME switch rather than a second hard-coded list, so a new status decides
  // its empty-list dot in one place and still trips the unknown-status warning.
  const emptyList = !hasMeds

  // Exhaustive over MedsStatus: adding a status to the union without deciding
  // its dot colour is then a compile error, not a medication list that quietly
  // looks fully reconciled (#563).
  switch (data.medsStatus) {
    case 'reconciling':
      // Nothing to warn about yet on an empty list: the run is still going.
      return emptyList ? null : 'bg-amber-400 animate-pulse'
    case 'preliminary':
      // Preliminary means the real run has not happened; with no medications
      // there is nothing yet to qualify.
      return emptyList ? null : 'bg-amber-400'
    case 'partial':
      return 'bg-amber-400'
    case 'failed':
      return 'bg-red-400'
    case 'reconciled':
      return emptyList ? null : 'bg-green-400'
    case null:
    case undefined:
      // Deliberate: no recorded reconciliation state is not a failure. Every
      // workspace predating #563's status column reads null, and so does a
      // medication list a clinician entered by hand — amber on all of those
      // would train the eye to ignore the dot. `undefined` joins null because
      // medsBanner (MedsTab.tsx) already reads the two the same way, and one
      // absent value cannot mean "nothing recorded" in the banner and
      // "unrecognised, warn" in the dot beside it.
      return emptyList ? null : 'bg-green-400'
    default: {
      // Compile-time exhaustiveness is unchanged: a new MedsStatus with no case
      // above is still a type error on this line. What changed is the RUNTIME
      // fallback — the backend can write a status this build has never heard of
      // — and returning `exhaustive` put that raw string into the className, so
      // the tab showed no dot at all: the failure mode this branch exists to
      // prevent. Amber is the honest default (something is unresolved).
      const exhaustive: never = data.medsStatus
      const seen = String(exhaustive)
      if (!warnedUnknownMedsStatuses.has(seen)) {
        warnedUnknownMedsStatuses.add(seen)
        console.warn(`PatientPanel: unknown medsStatus ${seen} — showing the amber dot`)
      }
      return 'bg-amber-400'
    }
  }
}

function ipassDot(data: PatientPanelData): DotColor {
  return data.ipassSource ? 'bg-green-400' : null
}

// ────────────────────────────────────────────────────────
// Tab config
// ────────────────────────────────────────────────────────

interface TabDef {
  id: PanelTab
  label: string
  dot: (data: PatientPanelData) => DotColor
}

const TABS: TabDef[] = [
  { id: 'summary', label: 'Summary', dot: summaryDot },
  { id: 'results', label: 'Results', dot: resultsDot },
  { id: 'orders', label: 'Orders', dot: ordersDot },
  { id: 'medications', label: 'Meds', dot: medsDot },
  { id: 'ipass', label: 'IPASS', dot: ipassDot },
]

// ────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────

export const PatientPanel: FC<PatientPanelProps> = ({
  data,
  onCollapseToHeader,
  onEdit,
  onClear,
  onReconcile,
  isReconciling = false,
  showToolbar = true,
  extractionProgress,
}) => {
  const [activeTab, setActiveTab] = useState<PanelTab>('summary')
  const [showMenu, setShowMenu] = useState(false)
  const [confirmingClear, setConfirmingClear] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
        setConfirmingClear(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Scroll to top when switching tabs
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 })
  }, [activeTab])

  // Two-click clear confirmation
  const handleClear = async () => {
    if (!onClear) return
    if (confirmingClear) {
      try {
        await onClear()
        setShowMenu(false)
        setConfirmingClear(false)
      } catch (err) {
        console.error('[PatientPanel] Failed to clear patient data:', err)
        setConfirmingClear(false)
      }
    } else {
      setConfirmingClear(true)
    }
  }

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-clip">
      {/* ── Tab bar ──────────────────────────────────────── */}
      <div className="flex items-center px-2 py-1 border-b border-border/30 bg-muted/30 flex-shrink-0">
        {/* Tabs - scrollable */}
        <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide">
          {TABS.map((tab) => {
            const dotColor = tab.dot(data)
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center px-2.5 py-1 rounded-md text-xs font-medium transition-colors duration-150',
                  activeTab === tab.id
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
                {dotColor && (
                  <span
                    className={cn('w-1.5 h-1.5 rounded-full ml-1.5 inline-block', dotColor)}
                  />
                )}
              </button>
            )
          })}
        </div>

        {/* Toolbar buttons - right-aligned, non-scrolling */}
        {showToolbar && (
          <div className="flex items-center gap-0.5 ml-auto flex-shrink-0 pl-1">
            {onCollapseToHeader && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={onCollapseToHeader}
                title="Collapse to header"
              >
                <PanelLeftClose className="h-3 w-3" />
              </Button>
            )}

            <div className="relative" ref={menuRef}>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={() => setShowMenu(!showMenu)}
              >
                <MoreHorizontal className="h-3 w-3" />
              </Button>

              {showMenu && (
                <div
                  className={cn(
                    'absolute right-0 top-full mt-1 z-50',
                    'w-52 py-1 rounded-lg',
                    'bg-popover border border-border shadow-lg',
                    'animate-in fade-in-0 zoom-in-95 duration-150',
                  )}
                >
                  {onEdit && (
                    <button
                      onClick={() => {
                        onEdit()
                        setShowMenu(false)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
                    >
                      <Edit3 className="h-4 w-4 text-muted-foreground" />
                      Edit patient data
                    </button>
                  )}

                  {onReconcile && (
                    <button
                      onClick={() => {
                        onReconcile()
                        setShowMenu(false)
                      }}
                      disabled={isReconciling}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors',
                        isReconciling && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      <RefreshCw
                        className={cn(
                          'h-4 w-4 text-muted-foreground',
                          isReconciling && 'animate-spin',
                        )}
                      />
                      {isReconciling ? 'Reconciling...' : 'Reconcile patient data'}
                    </button>
                  )}

                  {onClear && (
                    <>
                      <div className="h-px bg-border my-1" />
                      <button
                        onClick={handleClear}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                          confirmingClear
                            ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Trash2 className="h-4 w-4" />
                        {confirmingClear ? 'Click again to confirm' : 'Clear patient data'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Extraction progress banner ───────────────────── */}
      {extractionProgress?.isActive && (
        <div className="flex items-center gap-2 px-3 py-1.5 text-xs bg-primary/5 border-b border-primary/20 flex-shrink-0">
          <Loader2 className="h-3 w-3 text-primary animate-spin" />
          <span className="text-primary/80">{extractionProgress.message}</span>
        </div>
      )}

      {/* ── Tab content ──────────────────────────────────── */}
      <div
        ref={scrollContainerRef}
        className="flex-1 flex flex-col min-h-0 overflow-hidden"
      >
        <PanelErrorBoundary key={activeTab} label={activeTab}>
          {activeTab === 'summary' && <SummaryTab data={data} />}
          {activeTab === 'results' && <ResultsTab data={data} />}
          {activeTab === 'orders' && <OrdersTab data={data} />}
          {activeTab === 'medications' && <MedsTab data={data} />}
          {activeTab === 'ipass' && <IPASSTab data={data} />}
        </PanelErrorBoundary>
      </div>
    </div>
  )
}
